import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { installTelemetryGuard } from "./lib/telemetry-guard";

installTelemetryGuard();

// Exatamente um canonical/og:url, sempre em https://blog.logcomex.ai.
// Se o do Helmet (data-rh) for igual ao pré-renderizado, mantém o original
// intacto; se for diferente (navegação interna), substitui — nunca dois.
(() => {
  if (typeof document === "undefined") return;
  const SITE = "https://blog.logcomex.ai";
  const attrOf = (e: Element) => (e.tagName === "LINK" ? "href" : "content");
  const norm = (e: Element) => {
    const a = attrOf(e);
    const v = e.getAttribute(a) || "";
    if (v && !v.startsWith(SITE + "/") && v !== SITE) {
      try {
        const u = new URL(v, SITE);
        e.setAttribute(a, SITE + u.pathname + u.search);
      } catch { /* ignore */ }
    }
  };
  const dedupe = () => {
    for (const sel of ['link[rel="canonical"]', 'meta[property="og:url"]']) {
      const all = Array.from(document.head.querySelectorAll(sel));
      all.forEach(norm);
      if (all.length < 2) continue;
      const rh = all.filter((e) => e.hasAttribute("data-rh")).pop() || all[all.length - 1];
      const stat = all.find((e) => !e.hasAttribute("data-rh"));
      const same = stat && stat.getAttribute(attrOf(stat)) === rh.getAttribute(attrOf(rh));
      const keep = same ? stat! : rh;
      all.forEach((e) => e !== keep && e.remove());
    }
  };
  dedupe();
  new MutationObserver(dedupe).observe(document.head, { childList: true, subtree: true, attributes: true, attributeFilter: ["href", "content"] });
})();

// Early recovery redirect: if the URL carries Supabase password-recovery
// tokens, force navigation to /reset-password BEFORE the supabase client
// (which auto-detects the session in URL) is imported. Otherwise supabase-js
// consumes the code, strips it from the URL, and the user lands on whatever
// path the email link opened (e.g. site root / insights) already signed in.
(() => {
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/reset-password") return;
  const search = window.location.search;
  const hash = window.location.hash;
  const isRecovery =
    /[?&]code=/.test(search) ||
    (/[?&]type=recovery/.test(search) && /[?&]token_hash=/.test(search)) ||
    /type=recovery/.test(hash);
  if (isRecovery) {
    window.location.replace(`/reset-password${search}${hash}`);
  }
})();

// Auto-recover from stale dynamic import chunks after a redeploy.
// When the app shell references a chunk hash that no longer exists on the CDN,
// Vite throws "Failed to fetch dynamically imported module". Reload once to pick
// up the fresh index.html + new chunk hashes.
const CHUNK_RELOAD_KEY = "__chunk_reload_attempt__";
function isChunkLoadError(msg: string | undefined): boolean {
  if (!msg) return false;
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module")
  );
}
function maybeReloadForStaleChunk(msg: string | undefined) {
  if (!isChunkLoadError(msg)) return;
  try {
    const already = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (already) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    window.location.reload();
  } catch {
    window.location.reload();
  }
}
window.addEventListener("error", (e) => maybeReloadForStaleChunk(e?.message));
window.addEventListener("unhandledrejection", (e) => {
  const reason: any = (e as any)?.reason;
  const msg = typeof reason === "string" ? reason : reason?.message;
  maybeReloadForStaleChunk(msg);
});

// Remove o esqueleto estático (SSG) antes de montar o React e libera a página.
const rootEl = document.getElementById("root")!;
rootEl.innerHTML = "";
document.documentElement.classList.remove("js-hydrating");

createRoot(rootEl).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
